/**
 * @fileoverview Shared helpers for consistent click + touchend handling.
 */

const clickTouchHandlerMap = new WeakMap<Element, Map<Function, EventListener>>();

function getHandlerMap(el: Element): Map<Function, EventListener> {
    let map = clickTouchHandlerMap.get(el);
    if (!map) {
        map = new Map<Function, EventListener>();
        clickTouchHandlerMap.set(el, map);
    }
    return map;
}

/**
 * Add both click and touchend listeners with a shared wrapper.
 * Prevents default on touchend to avoid duplicate events on iOS.
 */
export function addClickAndTouch(el: Element, handler: (e: Event) => void): void {
    const map = getHandlerMap(el);
    if (map.has(handler)) return;

    const wrapped: EventListener = (e: Event) => {
        if (e && e.type === 'touchend') e.preventDefault();
        handler(e);
    };

    map.set(handler, wrapped);
    el.addEventListener('click', wrapped);
    el.addEventListener('touchend', wrapped, { passive: false });
}

/**
 * Remove both click and touchend listeners previously added with addClickAndTouch.
 */
export function removeClickAndTouch(el: Element, handler: (e: Event) => void): void {
    const map = clickTouchHandlerMap.get(el);
    const wrapped = map?.get(handler);
    if (!wrapped) return;

    el.removeEventListener('click', wrapped);
    el.removeEventListener('touchend', wrapped as EventListener);
    map?.delete(handler);
}
